import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import "../assets/users.css";
import "../assets/panels.css";
import "./GuardianQueryDetail.css";
import Loader from "../components/Loader";

const GuardianQueryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [query, setQuery] = useState(null);
  const [guardian, setGuardian] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [apiFailed, setApiFailed] = useState(false);

  useEffect(() => {
    loadGuardianQuery();
  }, [id]);

  // Helper function to check if current user is staff (Laravel equivalent)
  const isStaff = () => {
    if (!user || !user.roles) return false;
    const userRoles = Array.isArray(user.roles) ? user.roles : [user.roles];
    const staffRoles = [
      'SYSTEM-ADMINISTRATOR', 'SENIOR-MANAGEMENT', 'MANAGEMENT',
      'SALES', 'SALES WITH GUARDIANS', 'MAINTENANCE-MEDIUM',
      'GUARDIAN-MANAGER-WITH-MAINTENANCE', 'GUARDIAN-MAINTENANCE-CLIENT',
      'GUARDIAN-MANAGER-WITH-PROPERTIES-EDIT'
    ];
    return userRoles.some(role => staffRoles.includes(role));
  };

  // Helper function to check if current user is guardian
  const isGuardian = () => {
    if (!user || !user.userable_id) return false;
    const guardianTypes = ["App\\Guardians\\Guardian", "Guardian", "guardians"];
    return guardianTypes.includes(user.userable_type);
  };

  // Mock data for when API fails
  const getMockData = () => {
    const mockQuery = {
      id: parseInt(id),
      guardian_id: 1,
      status_id: 17, // Closed
      description:
        "This is a test query from the guardian to test the system functionality.",
      created_at: "2025-06-03 10:35:27",
      closed_at: "2025-06-12 10:40:00",
    };

    const mockGuardian = {
      id: 1,
      first_name: "Aime",
      last_name: "Guardian",
      email: "ashwin.iyer+guardian@theaimegroup.com",
      phone_number: "",
    };

    const mockReplies = [
      {
        id: 1,
        query_id: parseInt(id),
        user_id: 1,
        name: "Aime Guardian",
        is_gg: 0,
        description: "This is a test query",
        created_at: "2025-06-12 10:38:39",
        user_name: "Aime Guardian",
      },
      {
        id: 2,
        query_id: parseInt(id),
        user_id: 1,
        name: "Aime Guardian",
        is_gg: 0,
        description: "Okay thankyou",
        created_at: "2025-06-12 10:39:00",
        user_name: "Aime Guardian",
      },
      {
        id: 3,
        query_id: parseInt(id),
        user_id: 2,
        name: "Aime Test",
        is_gg: 1,
        description: "Thank you for your query. We'll get back to you soon.",
        created_at: "2025-06-12 10:39:31",
        user_name: "Aime Test",
      },
    ];

    return { mockQuery, mockGuardian, mockReplies };
  };

  const loadGuardianQuery = async () => {
    try {
      setLoading(true);
      setApiFailed(false);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/guardian-queries/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          console.log("API authentication failed, using mock data");
          setApiFailed(true);
          const { mockQuery, mockGuardian, mockReplies } = getMockData();
          setQuery(mockQuery);
          setGuardian(mockGuardian);
          setReplies(mockReplies);
          return;
        }
        throw new Error("Failed to load guardian query");
      }

      const data = await response.json();
      console.log("Guardian query detail data:", data);
      console.log("Guardian data from API:", data.guardian);

      setQuery(data.query);
      setGuardian(data.guardian);
      setReplies(data.replies || []);
      
      // Log if guardian data is missing
      if (!data.guardian || (!data.guardian.first_name && !data.guardian.last_name)) {
        console.warn("⚠️ Guardian data is missing or incomplete:", data.guardian);
      }
    } catch (err) {
      console.error("Error loading guardian query:", err);
      setError(err.message);

      // Use mock data on any error
      console.log("Using mock data due to API error");
      setApiFailed(true);
      const { mockQuery, mockGuardian, mockReplies } = getMockData();
      setQuery(mockQuery);
      setGuardian(mockGuardian);
      setReplies(mockReplies);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (statusId) => {
    const statusMap = {
      0: { text: "New query", color: "#006cff" },
      1: { text: "Global Guardians Replied", color: "#034ea2" },
      2: { text: "Guardian Replied", color: "#2FC694" },
      3: { text: "Closed", color: "#EF4444" },
      14: { text: "New query", color: "#006cff" },
      15: { text: "Global Guardians Replied", color: "#034ea2" },
      16: { text: "Guardian Replied", color: "#2FC694" },
      17: { text: "Closed", color: "#EF4444" },
    };

    const status = statusMap[statusId] || {
      text: `Status ${statusId}`,
      color: "#666",
    };

    return (
      <span
        className="status-badge"
        style={{ backgroundColor: status.color, color: "#fff" }}
      >
        {status.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return (
        date.toLocaleDateString("en-GB") +
        " " +
        date.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
      );
    } catch (error) {
      return dateString;
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;

    try {
      setSubmitting(true);

      if (apiFailed) {
        // Mock reply submission with proper role detection
        const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
        
        // Helper function to check if current user is guardian
        const isGuardianUser = () => {
          if (!currentUser || !currentUser.userable_type) return false;
          const guardianTypes = ["App\\Guardians\\Guardian", "Guardian", "guardians"];
          return guardianTypes.includes(currentUser.userable_type);
        };

        // Helper function to check if current user is staff
        const isStaffUser = () => {
          if (!currentUser.roles) return false;
          const userRoles = Array.isArray(currentUser.roles) ? currentUser.roles : [currentUser.roles];
          const staffRoles = [
            'SYSTEM-ADMINISTRATOR', 'SENIOR-MANAGEMENT', 'MANAGEMENT',
            'SALES', 'SALES WITH GUARDIANS', 'MAINTENANCE-MEDIUM',
            'GUARDIAN-MANAGER-WITH-MAINTENANCE', 'GUARDIAN-MAINTENANCE-CLIENT',
            'GUARDIAN-MANAGER-WITH-PROPERTIES-EDIT'
          ];
          return userRoles.some(role => staffRoles.includes(role));
        };

        const is_gg = isStaffUser() ? 1 : 0;
        const mockReply = {
          id: replies.length + 1,
          query_id: parseInt(id),
          user_id: currentUser.id || 2,
          name: `${currentUser.first_name || "User"} ${
            currentUser.last_name || "Name"
          }`,
          is_gg: is_gg,
          description: replyText,
          created_at: new Date()
            .toISOString()
            .replace("T", " ")
            .substring(0, 19),
          user_name: `${currentUser.first_name || "User"} ${
            currentUser.last_name || "Name"
          }`,
        };

        setReplies([...replies, mockReply]);
        setReplyText("");
        alert("Reply submitted successfully (mock mode)");
        return;
      }

      // Get current user info (you might need to adjust this based on your auth system)
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/guardian-queries/${id}/reply`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            description: replyText,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to submit reply");
      }

      const data = await response.json();
      console.log("Reply submitted:", data);

      setReplyText("");
      await loadGuardianQuery(); // Reload to get updated data
    } catch (err) {
      console.error("Error submitting reply:", err);
      alert("Failed to submit reply: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseQuery = async () => {
    if (!window.confirm("Are you sure you want to close this query?")) {
      return;
    }

    try {
      setClosing(true);

      if (apiFailed) {
        // Mock close operation
        setQuery((prev) => ({
          ...prev,
          status_id: 17,
          closed_at: new Date()
            .toISOString()
            .replace("T", " ")
            .substring(0, 19),
        }));
        alert("Query closed successfully (mock mode)");
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/guardian-queries/${id}/close`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to close query");
      }

      const data = await response.json();
      console.log("Query closed:", data);

      await loadGuardianQuery(); // Reload to get updated status
    } catch (err) {
      console.error("Error closing query:", err);
      alert("Failed to close query: " + err.message);
    } finally {
      setClosing(false);
    }
  };

  if (loading) return <Loader />;
  if (!query)
    return (
      <div className="container py-5 text-danger">Guardian query not found</div>
    );

  return (
    <div className="guardian-query-detail">
      {/* API Status Warning */}
      {apiFailed && (
        <div className="alert alert-warning" role="alert">
          <strong>⚠️ API Unavailable:</strong> Displaying mock data. Some
          features may not work as expected.
        </div>
      )}

      {/* Breadcrumbs */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><span onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer', color: '#337ab7' }}>Dashboard</span></li>
          <li className="breadcrumb-item"><span onClick={() => navigate('/guardian-queries')} style={{ cursor: 'pointer', color: '#337ab7' }}>Guardian Queries</span></li>
          <li className="breadcrumb-item active" aria-current="page">Guardian Query #{id}</li>
        </ol>
      </nav>

      <div className="query-container">
        {/* Header */}
        {/* <div className="query-header">
          <h1 className="query-title">Guardian Query</h1>
        </div> */}

        {/* Query Details */}
        <div className="query-details" style={{backgroundColor: 'rgba(248, 249, 250, 1)', padding: 16}}>
          <div className="query-header">
            <h1 className="query-title">Guardian Query</h1>
          </div>
          <div className="query-info">
            <div className="query-info-left">
              <div className="info-row">
                <span className="info-label">ID:</span>
                <span className="info-value">#{query.id}</span>
              </div>
              <div className="info-row">
                <span className="info-label">STATUS:</span>
                <span className="info-value">
                  {getStatusBadge(query.status_id)}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Created:</span>
                <span className="info-value">
                  {formatDate(query.created_at)}
                </span>
              </div>
            </div>
            <div className="query-info-right">
              <div className="info-row">
                <span className="info-label guardian-label">
                  Guardian Name:
                </span>
                <span className="info-value guardian-value">
                  {guardian && guardian.id ? (
                    <a
                      href={`/guardians/${guardian.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/guardians/${guardian.id}`);
                      }}
                      style={{
                        color: '#2FC694',
                        textDecoration: 'underline',
                        cursor: 'pointer'
                      }}
                    >
                      {guardian && guardian.first_name && guardian.last_name
                        ? `${guardian.first_name} ${guardian.last_name}`.trim()
                        : guardian && guardian.first_name
                        ? guardian.first_name
                        : guardian && guardian.id
                        ? `Guardian ${guardian.id}`
                        : "Guardian"}
                    </a>
                  ) : (
                    guardian && guardian.first_name && guardian.last_name
                      ? `${guardian.first_name} ${guardian.last_name}`.trim()
                      : guardian && guardian.first_name
                      ? guardian.first_name
                      : "Guardian"
                  )}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label guardian-label">
                  Guardian Email:
                </span>
                <span className="info-value guardian-value">
                  {guardian?.email && guardian.email !== "N/A" && guardian.email !== null
                    ? (
                      <a
                        href={`mailto:${guardian.email}`}
                        style={{
                          color: '#2FC694',
                          textDecoration: 'underline'
                        }}
                      >
                        {guardian.email}
                      </a>
                    )
                    : "N/A"}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label guardian-label">
                  Guardian Phone:
                </span>
                <span className="info-value guardian-value">
                  {guardian?.phone_number || ""}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Conversation */}
        <div className="conversation">
          {/* Initial Query - Guardian message with description */}
          <div className="message-block">
            <div className="message-bubble guardian-bubble">
              <div className="message-header">
                <div className="message-name">
                  {guardian && guardian.first_name && guardian.last_name
                    ? `${guardian.first_name} ${guardian.last_name}`.trim()
                    : guardian && guardian.first_name
                    ? guardian.first_name
                    : "Guardian"}
                </div>
                <div className="message-role">Guardian</div>
                <div className="message-time">
                  {formatDate(query.created_at)}
                </div>
              </div>
            </div>
            <div className="message-text">{query.description}</div>
          </div>

          {/* Replies */}
          {replies.map((reply, index) => (
            <div key={reply.id} className="message-block">
              {reply.is_gg ? (
                // Global Guardians reply (text on left, bubble on right)
                <>
                  <div className="message-text">{reply.description}</div>
                  <div className="message-bubble staff-bubble">
                    <div className="message-header">
                      <div className="message-name">
                        {reply.name || "Staff Member"}
                      </div>
                      <div className="message-role">Global Guardians</div>
                      <div className="message-time">
                        {formatDate(reply.created_at)}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                // Guardian reply (bubble on left, text on right)
                <>
                  <div className="message-bubble guardian-bubble">
                    <div className="message-header">
                      <div className="message-name">
                        {guardian && guardian.first_name && guardian.last_name
                          ? `${guardian.first_name} ${guardian.last_name}`.trim()
                          : guardian && guardian.first_name
                          ? guardian.first_name
                          : reply.name || "Guardian"}
                      </div>
                      <div className="message-role">Guardian</div>
                      <div className="message-time">
                        {formatDate(reply.created_at)}
                      </div>
                    </div>
                  </div>
                  <div className="message-text">{reply.description}</div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Reply Form - Laravel logic: show if not closed OR if closed but user is staff */}
        {(query.status_id !== 3 || (query.status_id === 3 && isStaff())) && query.status_id !== 17 && (
          <div className="reply-section" style={{backgroundColor: 'rgba(248, 249, 250, 1)', padding: 16}}>
            <h2 className="reply-title">REPLY</h2>
            <form onSubmit={handleSubmitReply} className="reply-form" >
              <div className="form-row">
                <label className="form-label">Description</label>
                <textarea
                  className="form-textarea"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Enter your reply..."
                  required
                />
              </div>
              <div className="form-actions" style={{ justifyContent: 'flex-end', borderWidth: 0}}>
                <button
                  type="submit"
                  className="submit-button"
                  style={{backgroundColor: '#1B2046'}}
                  disabled={submitting || !replyText.trim()}
                >
                  <span className="submit-icon">+</span>
                  <span className="submit-text">
                    {submitting ? "Submitting..." : "Submit"}
                  </span>
                </button>
                {/* Close button only visible for staff and if query is not already closed (Laravel logic) */}
                {isStaff() && query.status_id !== 3 && (
                  <button
                    type="button"
                    className="btn btn-danger close-button"
                    onClick={handleCloseQuery}
                    disabled={closing}
                    style={{ color: '#fff' }}
                  >
                    <i className="glyphicon glyphicon-cross"></i> {closing ? "Closing..." : "Close Query"}
                  </button>
                )}
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuardianQueryDetail;
